import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Message } from 'semantic-ui-react';
import { useHistory } from 'react-router-dom';

import { iRootState } from '../../store';
import { CustomDropDownOptions } from '../../types';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import {
  activatePropertiesWallet,
  createWalletProperty
} from '../../services/properties';
import Button from '../../components/Button';

import {
  buildInitialFilters,
  propertiesWalletLabel,
  walletPropertyTypeOptions
} from './constants';
import * as Storage from '../../services/storage';
import { usePropertiesWalletList } from './hooks/usePropertiesWalletList';
import FiltersBar from './components/FiltersBar';
import PropertiesTable from './components/PropertiesTable';
import CreatePropertyModal, {
  CreatePropertyFormValues
} from './components/CreatePropertyModal';
import PropertyDetailsModal from './components/PropertyDetailsModal';
import { PropertyWalletItemDTO } from '../../services/properties';
import {
  ActivationError,
  ActivationMessage,
  ActivationText,
  ContentSegment,
  PageContainer,
  PageHeader,
  PageSubtitle,
  PageTitle
} from './styles';

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

type Props = ReturnType<typeof mapState>;

const PropertiesWalletPage = ({ availableBrokersAndEstates, user }: Props) => {
  const history = useHistory();
  const role = user?.role as UserRolesEnum | undefined;
  const isEstateUser = (role || '').includes('ESTATE');
  const isLadoBom = Storage.get('external_login') === 'true';
  const defaultEstateId = useMemo(() => {
    return (
      availableBrokersAndEstates?.activeEstateId ||
      user?.estate?.id ||
      ''
    );
  }, [availableBrokersAndEstates?.activeEstateId, user?.estate?.id]);

  const initialFilters = useMemo(
    () => buildInitialFilters(isEstateUser ? defaultEstateId : defaultEstateId),
    [defaultEstateId, isEstateUser]
  );

  const estateOptions = useMemo(() => {
    return (availableBrokersAndEstates?.estates || []).map((item: CustomDropDownOptions) => {
      const valueId =
        typeof item.value === 'string' ? item.value : item.value?.id;
      return {
        key: valueId || String(item.key),
        text: item.text || '',
        value: valueId || ''
      };
    });
  }, [availableBrokersAndEstates?.estates]);

  const baseListEnabled = isEstateUser || Boolean(defaultEstateId);

  const {
    isLoading,
    items,
    total,
    estateContext,
    currentPage,
    setCurrentPage,
    filters,
    setFilters,
    appliedFilters,
    listEnabled,
    errorMessage,
    isScopeError,
    loadData,
    handleSearch,
    clearFilters
  } = usePropertiesWalletList({
    initialFilters,
    enabled: baseListEnabled
  });

  const [isActivatingWallet, setIsActivatingWallet] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreatingProperty, setIsCreatingProperty] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<PropertyWalletItemDTO | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const hasActiveFilters = Boolean(appliedFilters.search || appliedFilters.type);
  const shouldShowActivateButton =
    listEnabled && !isLoading && !errorMessage && Boolean(estateContext && !estateContext.rvUuid);
  const canCreateProperty = listEnabled && !shouldShowActivateButton;
  const selectedEstateId =
    appliedFilters.estateId || filters.estateId || defaultEstateId || undefined;

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error && error.message) return error.message;
    if (typeof error === 'object' && error !== null) {
      const maybeMessage = (error as { message?: string | string[] }).message;
      if (Array.isArray(maybeMessage)) return maybeMessage.join(', ');
      if (typeof maybeMessage === 'string' && maybeMessage.trim()) return maybeMessage;
    }
    return 'Não foi possível cadastrar o imóvel agora. Tente novamente.';
  };

  const handleActivateWallet = async () => {
    try {
      setActivationError(null);
      setIsActivatingWallet(true);
      await activatePropertiesWallet(appliedFilters.estateId || filters.estateId || defaultEstateId);
      setCurrentPage(1);
      await loadData(1, appliedFilters);
    } catch (error) {
      setActivationError(
        error instanceof Error
          ? error.message
          : 'Não foi possível ativar a carteira agora. Tente novamente.'
      );
    } finally {
      setIsActivatingWallet(false);
    }
  };

  const handleCreateProperty = async (values: CreatePropertyFormValues) => {
    try {
      setCreateError(null);
      setCreateSuccess(null);
      setIsCreatingProperty(true);

      if (!isEstateUser && !selectedEstateId) {
        throw new Error('Selecione a imobiliária para cadastrar o imóvel.');
      }

      const area = Number(String(values.area || '').replace(',', '.'));
      const normalizedArea = Number.isNaN(area) ? 0 : area;
      const normalizedIdentification =
        values.identification.trim() ||
        `${values.street.trim()} ${values.number.trim()}`.trim() ||
        values.zipcode.trim();

      await createWalletProperty({
        estateId: isEstateUser ? undefined : selectedEstateId,
        identification: normalizedIdentification,
        propertyType: values.propertyType,
        area: normalizedArea,
        address: {
          zipcode: values.zipcode.trim(),
          street: values.street.trim(),
          neighborhood: values.neighborhood.trim(),
          number: values.number.trim(),
          complement: values.complement.trim() || undefined,
          city: values.city.trim(),
          state: values.state.trim().toUpperCase()
        }
      });

      setIsCreateModalOpen(false);
      setCreateSuccess('Imóvel cadastrado com sucesso.');
      setCurrentPage(1);
      await loadData(1, appliedFilters);
    } catch (error) {
      const message = getErrorMessage(error);
      setCreateError(message);
      throw error;
    } finally {
      setIsCreatingProperty(false);
    }
  };

  const openCreateModal = () => {
    setCreateError(null);
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    if (isCreatingProperty) return;
    setIsCreateModalOpen(false);
    setCreateError(null);
  };

  const handleOpenPropertyDetails = (item: PropertyWalletItemDTO) => {
    setSelectedProperty(item);
    setIsDetailsOpen(true);
  };

  const handleClosePropertyDetails = () => {
    setIsDetailsOpen(false);
    setSelectedProperty(null);
  };

  return (
    <PageContainer>
      <FiltersBar
        isEstateUser={isEstateUser}
        estateOptions={estateOptions}
        typeOptions={walletPropertyTypeOptions}
        filters={filters}
        setFilters={setFilters}
        onSearch={handleSearch}
        onClear={clearFilters}
        showCreateButton={canCreateProperty}
        onCreateClick={openCreateModal}
        onImportClick={() => history.push('/admin/properties/import')}
      />

      <ContentSegment>
        {createSuccess && (
          <Message
            positive
            onDismiss={() => setCreateSuccess(null)}
            content={createSuccess}
            style={{ marginBottom: 16 }}
          />
        )}

        {shouldShowActivateButton && (
          <ActivationMessage info>
            <ActivationText>
              Ative a carteira de imóveis para iniciar a sincronização dos imóveis da sua imobiliária.
            </ActivationText>
            <Button
              type="button"
              loading={isActivatingWallet}
              disabled={isActivatingWallet}
              onClick={handleActivateWallet}
              text="Ativar carteira"
            />
            {activationError && <ActivationError>{activationError}</ActivationError>}
          </ActivationMessage>
        )}

        {!shouldShowActivateButton && (
          <PropertiesTable
            isLoading={isLoading}
            items={items}
            currentPage={currentPage}
            total={total}
            setCurrentPage={setCurrentPage}
            errorMessage={errorMessage}
            isScopeError={isScopeError}
            hasActiveFilters={hasActiveFilters}
            listEnabled={listEnabled}
            onOpenDetails={handleOpenPropertyDetails}
          />
        )}
      </ContentSegment>

      <PropertyDetailsModal
        open={isDetailsOpen}
        property={selectedProperty}
        estateId={selectedEstateId}
        onClose={handleClosePropertyDetails}
      />

      <CreatePropertyModal
        open={isCreateModalOpen}
        isSubmitting={isCreatingProperty}
        isLadoBom={isLadoBom}
        userId={user?.id}
        submitError={createError}
        onClose={closeCreateModal}
        onSubmit={handleCreateProperty}
      />
    </PageContainer>
  );
};

export default connect(mapState, null)(PropertiesWalletPage);
